import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hook';
import FleetPlanningAPI from 'api/fleet-planning.api';

export type DispatchMode = 'manual' | 'auto';

export default function useDispatchMode(): {
  mode: DispatchMode;
  setMode: (next: DispatchMode) => Promise<void>;
  loading: boolean;
} {
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const fleetPlanningApi = useRef(new FleetPlanningAPI()).current;
  // backend default is "manual" when unset — mirror that default here so every
  // consumer renders correctly before the fetch resolves
  const [mode, setModeState] = useState<DispatchMode>('manual');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    setLoading(true);
    fleetPlanningApi
      .getDispatchMode(customerId)
      .then(() => {
        // Auto/Manual toggle is hidden app-wide — dispatch mode is fixed to
        // 'manual' regardless of what the backend has persisted, so the
        // fetched value is intentionally not applied here.
        // if (!cancelled) setModeState(res.dispatchMode);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, fleetPlanningApi]);

  const setMode = useCallback(
    async (next: DispatchMode) => {
      if (!customerId) return;
      const previous = mode;
      setModeState(next);
      try {
        await fleetPlanningApi.setDispatchMode(customerId, next);
      } catch (e) {
        setModeState(previous);
        throw e;
      }
    },
    [customerId, fleetPlanningApi, mode]
  );

  return { mode, setMode, loading };
}
